<template>
    <div id="content">
        <v-overlay :model-value="signupInProgress" absolute class="align-center justify-center">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>

        <v-card class="mx-auto mt-10 px-12 py-8" elevation="2" max-width="648" rounded="lg">
            <v-container>
                <v-row>
                    <v-col>
                        <a href="https://kidsout.org.uk">
                            <v-img :src="`/common/img/Kids_out_logo.jpeg`" alt="Kids Out" />
                        </a>
                    </v-col>
                    <v-col>
                        <v-img :src="`/common/img/ws-big.png`" alt="World Stories" />
                        <h3 class="text-primary text-center mb-10">with Giglets Education</h3>
                    </v-col>
                </v-row>
            </v-container>

            <template v-if="signupDone">
                <h2>Thank you!</h2>
                <p class="mb-3">
                    Thank you for your interest in WorldStories. A welcome e-mail will be sent out shortly. <br />
                </p>
                <p>You may log in <a :href="`${baseUrl}landing`">here</a>.</p>
            </template>
            <template v-else>
                <p>
                    WorldStories is a growing collection of traditional and original stories from around the world
                    representing the languages most commonly spoken by children in the UK.
                </p>
                <p class="mt-3 mb-5">
                    Are you a teacher and keen to use WorldStories to support the children in your class? Register now
                    for free access to illustrated stories in many languages.
                </p>

                <v-form @submit.prevent="signup">
                    <div class="text-subtitle-1 text-medium-emphasis">Your name</div>
                    <v-text-field
                        v-model="signupName"
                        density="compact"
                        prepend-inner-icon="mdi-account-outline"
                        variant="outlined"
                        name="name"
                    ></v-text-field>

                    <div class="text-subtitle-1 text-medium-emphasis">Your email address</div>
                    <v-text-field
                        v-model="signupEmailAddress"
                        density="compact"
                        prepend-inner-icon="mdi-email-outline"
                        variant="outlined"
                        name="emailaddress"
                    ></v-text-field>

                    <div class="text-subtitle-1 text-medium-emphasis">Your curriculum</div>
                    <v-select
                        v-model="signupCurriculum"
                        density="compact"
                        variant="outlined"
                        name="curricula"
                        :items="curricula"
                        prepend-inner-icon="mdi-book-open-outline"
                    >
                    </v-select>

                    <div class="text-subtitle-1 text-medium-emphasis">Account type</div>
                    <v-select
                        v-model="signupUserType"
                        density="compact"
                        variant="outlined"
                        name="type"
                        :items="userTypes"
                        prepend-inner-icon="mdi-account-school-outline"
                    >
                    </v-select>

                    <v-checkbox
                        v-model="signupWsUpdates"
                        label="Tick this box to receive updates about WorldStories."
                        dense
                    ></v-checkbox>

                    <v-alert v-if="error" variant="outlined" :text="error" type="error"></v-alert>

                    <div class="mt-6 d-flex flex-column align-center">
                        <v-btn
                            block
                            variant="flat"
                            color="primary"
                            size="large"
                            type="submit"
                            :loading="signupInProgress"
                            :disabled="signupInProgress"
                            >Finish</v-btn
                        >
                        <div class="mt-3 text-subtitle-1">
                            Already use WorldStories?
                            <a class="text-decoration-none text-blue" :href="`${baseUrl}landing`">Log in</a>
                        </div>
                    </div>
                </v-form>
                <div class="d-flex justify-space-between px-3 mt-10">
                    <a class="text-caption text-decoration-none text-blue" href="https://www.giglets.com/Legal"
                        >End User Licence Agreement</a
                    >
                    <a class="text-caption text-decoration-none text-blue" href="https://www.giglets.com/Legal"
                        >Access Licence Agreement</a
                    >
                </div>
            </template>
        </v-card>
    </div>
</template>

<script setup lang="ts">
import { FetchError } from 'ofetch';

definePage({
    meta: {
        layout: 'bare',
        noAuth: true,
    },
});

const signupInProgress = ref(false);
const signupDone = ref(false);
const error = ref<string | undefined>();

const baseUrl = import.meta.env.BASE_URL;
const router = useRouter();
const route = useRoute();
const queryName = typeof route.query.signup_name === 'string' ? route.query.signup_name : '';
const queryEmail = typeof route.query.signup_email === 'string' ? route.query.signup_email : '';
const queryCurriculum = typeof route.query.signup_curriculum === 'string' ? route.query.signup_curriculum : '';
const queryUserType = typeof route.query.signup_accounttype === 'string' ? route.query.signup_accounttype : 'teacher';
const queryUpdates = route.query.signup_receive_ws_updates === 'on';

const userTypes = [
    { value: 'notateacher', title: 'Not a teacher' },
    { value: 'teacher', title: 'Teacher' },
];
const curricula = [
    { value: 'NC', title: 'England - National Curriculum' },
    { value: 'PLC', title: 'Ireland - Primary Language Curriculum' },
    { value: 'PLC', title: 'Northern Ireland - Northern Ireland Curriculum' },
    { value: 'CfE', title: 'Scotland - Curriculum for Excellence' },
    { value: 'LNF', title: 'Wales - Curriculum for Wales' },
    { value: 'NC', title: 'Other' },
];
const signupUserType = ref<string>(queryUserType);
const signupCurriculum = ref<string>(queryCurriculum);
const signupName = ref<string>(queryName);
const signupEmailAddress = ref<string>(queryEmail);
const signupWsUpdates = ref<boolean>(queryUpdates);

onMounted(async () => {
    if (!(queryName && queryEmail && queryCurriculum && queryUserType)) {
        return;
    }
    /*
    Clear the query before signing up. The signup request takes several seconds, and while it is in
    flight the address bar must no longer carry the payload - otherwise a reload re-mounts this page
    and sends an identical signup request.
     */
    await router.replace({ query: {} });
    await signup();
});

async function signup() {
    if (signupInProgress.value || signupDone.value) {
        return;
    }
    error.value = '';
    try {
        signupInProgress.value = true;
        if (!signupName.value) {
            error.value = 'Please enter a name';
            return;
        }
        if (!signupEmailAddress.value) {
            error.value = 'Please enter an email address';
            return;
        }
        if (!signupEmailAddress.value.includes('@') || !signupEmailAddress.value.includes('.')) {
            error.value = 'Please enter a valid email address';
            return;
        }
        if (!signupCurriculum.value) {
            error.value = 'Please select a curriculum';
            return;
        }
        if (!signupUserType.value) {
            error.value = 'Please select an account type';
            return;
        }
        await $fetch('/api/ws/signup', {
            method: 'POST',
            body: {
                name: signupName.value,
                email: signupEmailAddress.value,
                userType: signupUserType.value,
                curriculum: signupCurriculum.value,
                updates: signupWsUpdates.value,
            },
        });
        signupDone.value = true;
    } catch (err) {
        const fetchError = err as FetchError;
        if (fetchError.data && fetchError.data.message) {
            error.value = fetchError.data.message;
            return;
        }
        throw err;
    } finally {
        signupInProgress.value = false;
    }
}
</script>
<style scoped lang="scss">
.text-subtitle-1 {
    font-family: 'HeinemannSpecial-Roman', sans-serif !important;
}
:deep(.v-field__input) {
    margin-top: 5px;
}
.home-logo {
    width: 250px;
    margin: 50px auto 0 auto;
}
</style>
